import {createStyles} from '@mantine/core';
import React, {useMemo, useState} from 'react';

import AgentApprovalCard from 'llm/chat-library/messages/AgentApprovalCard';
import {
  ChatAssistantReportMessageContents,
  SaveChatAssistantReplyOverflowButton,
  SaveChatAssistantReportButton,
} from 'llm/chat-library/messages/ChatAssistantMessageMenu';
import {ProgressData} from 'llm/chat-library/progress/ProgressTypes';
import LLMFeedbackChatToolbar from 'llm/feedback/LLMFeedbackChatToolbar';
import LLMLoadingMessage from 'llm/loading/LLMLoadingMessage';
import LLMMarkdownSection, {LLMMarkdownClassNames} from 'llm/markdown/LLMMarkdownSection';
import {assertNever} from 'util/Types';

import {ChatMessageBubble, ChatMessageToolbar} from './ChatMessageBubble';
import {AssistantMessage, DisplayMessageAssistantResponseType} from './MessageTypes';
import {getDisplayMessageAssistantResponseType, isApprovalRequestMessage} from './MessageUtils';

const useChatAssistantMarkdownStyles = createStyles((theme) => ({
  chip: {
    marginRight: 8,
    padding: '1px 4px 2px',
    lineHeight: 1,
  },
  tableCopyButton: {
    right: 10,
    height: 43,
  },
  // Compound selectors beat global simple-table.less (same specificity as the old .ai-report overrides).
  simpleTableHeader: {
    '&.simpleTable-header': {
      height: 43,
    },
  },
  simpleTableHeaderCell: {
    '&.simpleTable-header-cell': {
      color: theme.colors.gray[7],
      fontSize: 13,
      lineHeight: '16px',
      fontWeight: 600,
      padding: '10px 12px 10px',
    },
  },
  simpleTableRow: {
    '&.simpleTable-row': {
      height: 30,
      '& td.simpleTable-cell': {
        padding: '6px 12px 6px',
      },
    },
  },
}));

export interface ChatAssistantMessageBubbleProps {
  event: AssistantMessage;
  progressData?: ProgressData;
  citationData?: ProgressData;
  /** If true, hides interactive components with side-effects. */
  readonly?: boolean;
  chatSessionId: string;
  /** Session display name when loaded; used for default save entry / file names. */
  chatSessionDisplayName?: string | null;
}

function getToolbarActions(
  event: AssistantMessage,
  {
    readonly,
    chatSessionId,
    chatSessionDisplayName,
    markdown,
    onReplyOverflowOpenChange,
  }: {
    readonly: boolean;
    chatSessionId: string;
    chatSessionDisplayName?: string | null;
    markdown: string;
    onReplyOverflowOpenChange: (isOpen: boolean) => void;
  }
): React.ReactNode {
  const responseType = getDisplayMessageAssistantResponseType(event);
  switch (responseType) {
    case DisplayMessageAssistantResponseType.REPORT: {
      const messageText = getMarkdown(event);
      return !readonly ? (
        <SaveChatAssistantReportButton
          markdownReport={messageText}
          chatSessionId={chatSessionId}
          chatSessionDisplayName={chatSessionDisplayName}
          assistantMessage={event}
        />
      ) : null;
    }
    case DisplayMessageAssistantResponseType.REPLY:
      return !readonly ? (
        <SaveChatAssistantReplyOverflowButton
          markdownReport={markdown}
          chatSessionId={chatSessionId}
          chatSessionDisplayName={chatSessionDisplayName}
          assistantMessage={event}
          onOpenChange={onReplyOverflowOpenChange}
        />
      ) : null;
    default:
      assertNever(responseType);
      return null;
  }
}

function getMarkdown(event: AssistantMessage): string {
  return event.messageBlocks
    .flatMap((block) => {
      switch (block.__typename) {
        case 'DisplayTextBlockNode':
          return block.text;
        case 'DisplayFileBlockNode':
          // Ignore file blocks for now. Deep Research returns file blocks when the image generation tool is called.
          // These are rendered inline using bnch_file tags, so we shouldn't render them here.
          return [];
        default:
          assertNever(block);
          return [];
      }
    })
    .join('\n');
}

export default function ChatAssistantMessageBubble({
  event,
  progressData,
  citationData,
  readonly = false,
  chatSessionId,
  chatSessionDisplayName = null,
}: ChatAssistantMessageBubbleProps): JSX.Element {
  const [isReplyOverflowOpen, setIsReplyOverflowOpen] = useState(false);
  const {classes} = useChatAssistantMarkdownStyles();

  // A write-approval request renders like a normal assistant reply, with a distinct approval system
  // card appended inside the message border. Derived directly from the event that carries the
  // request, so callers don't need to special-case the message type.
  const footer =
    event.__typename === 'DisplayMessageChatEventNode' && isApprovalRequestMessage(event) ? (
      <AgentApprovalCard chatSessionId={chatSessionId} requestEventId={event.id} readonly={readonly} />
    ) : null;

  const markdownClassNames: LLMMarkdownClassNames = useMemo(
    () => ({
      chip: classes.chip,
      tableCopyButton: classes.tableCopyButton,
      simpleTableHeader: classes.simpleTableHeader,
      simpleTableHeaderCell: classes.simpleTableHeaderCell,
      simpleTableRow: classes.simpleTableRow,
    }),
    [classes]
  );

  // Implicitly, message blocks are markdown. In the future, that might not be the case, and the block
  // itself will indicate its type.
  const markdown = useMemo(() => getMarkdown(event), [event]);
  const responseType = getDisplayMessageAssistantResponseType(event);

  const toolbar = useMemo(
    () => (
      <ChatMessageToolbar
        left={
          // Only render the feedback toolbar for persisted messages.
          !readonly && event.__typename === 'DisplayMessageChatEventNode' ? (
            <LLMFeedbackChatToolbar
              chatEventId={event.id}
              chatEventCreatedAt={event.createdAt}
              agentType={event.agentType}
            />
          ) : (
            <div>
              {event.__typename === 'ClientStreamingAssistantMessage' && event.progressMessage ? (
                <LLMLoadingMessage text={event.progressMessage} />
              ) : null}
            </div>
          )
        }
        right={getToolbarActions(event, {
          readonly,
          chatSessionId,
          chatSessionDisplayName,
          markdown,
          onReplyOverflowOpenChange: setIsReplyOverflowOpen,
        })}
        hideRightUntilHover={responseType === DisplayMessageAssistantResponseType.REPLY}
        showRightAction={responseType !== DisplayMessageAssistantResponseType.REPLY || isReplyOverflowOpen}
      />
    ),
    [event, readonly, chatSessionId, chatSessionDisplayName, markdown, responseType, isReplyOverflowOpen]
  );

  switch (responseType) {
    case DisplayMessageAssistantResponseType.REPLY:
      return (
        <ChatMessageBubble variant="reply" toolbar={toolbar} footer={footer}>
          <LLMMarkdownSection
            markdown={markdown}
            chatSessionId={chatSessionId}
            classNames={markdownClassNames}
            canSaveChartsToEntry={!readonly}
          />
        </ChatMessageBubble>
      );
    case DisplayMessageAssistantResponseType.REPORT:
      return (
        <ChatMessageBubble variant="report" toolbar={toolbar}>
          <ChatAssistantReportMessageContents
            report={markdown}
            progressData={progressData}
            citationData={citationData}
            readonly={readonly}
            chatSessionId={chatSessionId}
            markdownClassNames={markdownClassNames}
          />
        </ChatMessageBubble>
      );
    default:
      assertNever(responseType);
      // NB: This should never be exercised as responseType is generated on the client.
      // If we move this to be a persisted field, this fallback may be exercised.
      return (
        <ChatMessageBubble variant="reply" toolbar={toolbar} footer={footer}>
          <LLMMarkdownSection
            markdown={markdown}
            chatSessionId={chatSessionId}
            classNames={markdownClassNames}
            canSaveChartsToEntry={!readonly}
          />
        </ChatMessageBubble>
      );
  }
}
